import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  Ruling,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';

const RULING_FIELDS = gql`
  fragment RulingFields on Ruling {
    id
    title
    description
    type
    isActive
    cards {
      id
      konamiId
      name
      image
    }
    examples {
      id
      text
      cards {
        id
        konamiId
        name
        image
      }
    }
    sources {
      id
      url
      label
    }
    createdAt
    updatedAt
    createdBy {
      id
      name
      lastName
    }
    updatedBy {
      id
      name
      lastName
    }
  }
`;

export type RulingFilters = {
  search?: string;
  type?: string;
  isActive?: boolean;
  cardKonamiId?: number;
  page?: number;
  limit?: number;
};

export const getRulings = async (
  user: Nullable<UserWithToken>,
  filters: RulingFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Ruling>>(
    gql`
      ${RULING_FIELDS}
      query rulings($filters: RulingFilters) {
        data: rulings(filters: $filters) {
          data {
            ...RulingFields
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getRuling = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<Ruling | null>>(
    gql`
      ${RULING_FIELDS}
      query ruling($id: Int!) {
        data: ruling(id: $id) {
          ...RulingFields
        }
      }
    `,
    { id }
  );

export type RulingExampleInput = { text: string; cardKonamiIds?: number[] };
export type RulingSourceInput = { url: string; label?: string };

export type CreateRulingInput = {
  title: string;
  description: string;
  type?: string;
  isActive?: boolean;
  cardKonamiIds?: number[];
  examples?: RulingExampleInput[];
  sources?: RulingSourceInput[];
};

export const createRuling = async (
  user: Nullable<UserWithToken>,
  input: CreateRulingInput
) =>
  getGraphQLQueryClient(user).request<WithData<Ruling>>(
    gql`
      ${RULING_FIELDS}
      mutation createRuling($input: CreateRulingInput!) {
        data: createRuling(input: $input) {
          ...RulingFields
        }
      }
    `,
    { input }
  );

export type UpdateRulingInput = Partial<CreateRulingInput>;

export const updateRuling = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: UpdateRulingInput
) =>
  getGraphQLQueryClient(user).request<WithData<Ruling>>(
    gql`
      ${RULING_FIELDS}
      mutation updateRuling($id: Int!, $input: UpdateRulingInput!) {
        data: updateRuling(id: $id, input: $input) {
          ...RulingFields
        }
      }
    `,
    { id, input }
  );

export const deleteRuling = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<Ruling>>(
    gql`
      ${RULING_FIELDS}
      mutation deleteRuling($id: Int!) {
        data: deleteRuling(id: $id) {
          ...RulingFields
        }
      }
    `,
    { id }
  );
